import IconBigArrowLeft from '@assets/icons/big arrow left.svg?react';
import IconBigArrowRight from '@assets/icons/big arrow right.svg?react';
/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
import IconClose from '@assets/icons/close.svg?react';
import IconSave from '@assets/icons/save.svg?react';
import {
	AppBar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	IconButton,
	Slide,
	type Theme,
	Toolbar,
	Typography,
	useTheme,
} from '@mui/material';
import type { TransitionProps } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useAppDispatch } from '../../../redux/hooks';
import { getResultPicturesStart } from '../../../redux/results/resultsSlice';
import { getSpyPhotosStart } from '../../../redux/spy/spySlice';
import type { RootState } from '../../../redux/store';
import { createDataUrl } from '../../../utils/base64File';
import { titleStyles } from './infoStyles';

// Normalized photo shape shared by both galleries (peak photos + result pictures).
type GalleryPhoto = {
	Id: number;
	name: string;
	thumbUrl: string;
	fullUrl: string;
};

// The peakphotos endpoint returns no MIME type, so derive it from the file name.
const mimeFromName = (name?: string): string => {
	const ext = name?.split('.').pop()?.toLowerCase();
	switch (ext) {
		case 'png':
			return 'image/png';
		case 'gif':
			return 'image/gif';
		case 'webp':
			return 'image/webp';
		case 'bmp':
			return 'image/bmp';
		default:
			return 'image/jpeg';
	}
};

const Transition = React.forwardRef(
	(
		props: TransitionProps & {
			children: React.ReactElement<unknown>;
		},
		ref: React.Ref<unknown>,
	) => <Slide direction="up" ref={ref} {...props} />,
);
Transition.displayName = 'Transition';

const PhotoModal = ({
	open,
	handleOpen,
	photos,
	currentIndex,
	onPrevious,
	onNext,
}: {
	open: boolean;
	handleOpen: (is: boolean) => void;
	photos: GalleryPhoto[];
	currentIndex: number;
	onPrevious: () => void;
	onNext: () => void;
}) => {
	const { t } = useTranslation();
	const th = useTheme();
	const photo = photos[currentIndex];
	const hasPrevious = currentIndex > 0;
	const hasNext = currentIndex < photos.length - 1;
	const photoDataUrl = photo?.fullUrl;
	const photoDownloadName = photo?.name || `${photo?.Id}.jpg`;

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'ArrowLeft' && hasPrevious) {
			onPrevious();
		} else if (event.key === 'ArrowRight' && hasNext) {
			onNext();
		} else if (event.key === 'Escape') {
			handleOpen(false);
		}
	};

	return (
		<Dialog
			fullScreen
			open={open}
			onClose={() => handleOpen(false)}
			TransitionComponent={Transition}
			onKeyDown={handleKeyDown}
			tabIndex={0}
		>
			<AppBar
				sx={{
					position: 'relative',
					background: (theme: Theme) => theme.palette.kemek.white,
					boxShadow: 'none',
				}}
			>
				<Toolbar sx={{ display: 'flex', alignItems: 'center' }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							width: '100%',
							gap: 5,
							fontSize: 18,
						}}
					>
						<Typography sx={{ fontSize: 'inherit', whiteSpace: 'nowrap' }}>
							{photo?.name}
						</Typography>
						<Typography
							sx={{
								fontSize: 'inherit',
								whiteSpace: 'nowrap',
								color: (theme: Theme) => theme.palette.kemek.gray,
							}}
						>
							{currentIndex + 1}/{photos.length}
						</Typography>
						<Box sx={{ display: 'flex', gap: 2, ml: 'auto', mr: 'auto' }}>
							<Button
								component="a"
								variant="outlined"
								sx={{ height: 40 }}
								href={photoDataUrl}
								download={photoDownloadName}
								endIcon={<IconSave />}
							>
								{t('global.download')}
							</Button>
						</Box>
					</Box>
					<IconButton
						edge="start"
						color="inherit"
						title={t('global.close')}
						onClick={() => {
							handleOpen(false);
						}}
						aria-label={t('global.close')}
					>
						<IconClose
							style={{
								width: 20,
								height: 20,
								color: th.palette.kemek.darkestGray,
							}}
						/>
					</IconButton>
				</Toolbar>
			</AppBar>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					maxHeight: 'calc(100% - 64px)',
					width: '100%',
					background: th.palette.kemek.gray,
					flex: 1,
					position: 'relative',
				}}
			>
				{hasPrevious && (
					<IconButton
						onClick={onPrevious}
						title={t('global.previousImageAriaLabel')}
						sx={{
							position: 'absolute',
							left: 16,
							zIndex: 1,
							backgroundColor: 'rgba(255, 255, 255, 0.8)',
							'&:hover': {
								backgroundColor: 'rgba(255, 255, 255, 0.9)',
							},
						}}
						aria-label={t('global.previousImageAriaLabel')}
					>
						<IconBigArrowLeft
							style={{
								width: 24,
								height: 24,
								color: th.palette.kemek.darkestGray,
							}}
						/>
					</IconButton>
				)}
				{hasNext && (
					<IconButton
						onClick={onNext}
						title={t('global.nextImageAriaLabel')}
						sx={{
							position: 'absolute',
							right: 16,
							zIndex: 1,
							backgroundColor: 'rgba(255, 255, 255, 0.8)',
							'&:hover': {
								backgroundColor: 'rgba(255, 255, 255, 0.9)',
							},
						}}
						aria-label={t('global.nextImageAriaLabel')}
					>
						<IconBigArrowRight
							style={{
								width: 24,
								height: 24,
								color: th.palette.kemek.darkestGray,
							}}
						/>
					</IconButton>
				)}
				<Link
					to={photoDataUrl}
					target="_blank"
					style={{
						display: 'block',
						height: '100%',
						maxWidth: '100%',
					}}
					download={photoDownloadName}
				>
					<img
						src={photoDataUrl}
						alt={t('global.imageAlt')}
						style={{ display: 'block', height: '100%' }}
					/>
				</Link>
			</Box>
		</Dialog>
	);
};

const PhotoBox = ({
	photo,
	small,
	onClick,
	overlayCount,
}: {
	photo: GalleryPhoto;
	small?: boolean;
	onClick: () => void;
	overlayCount?: number;
}) => {
	return (
		<Box
			sx={{
				position: 'relative',
				width: '100%',
				height: small ? '54px' : '177px',
				borderRadius: '4px',
				backgroundImage: `url(${photo?.thumbUrl})`,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				backgroundRepeat: 'no-repeat',
				cursor: 'pointer',
				overflow: 'hidden',
			}}
			onClick={onClick}
		>
			{overlayCount && overlayCount > 0 ? (
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: 'rgba(0, 0, 0, 0.55)',
						color: (theme: Theme) => theme.palette.kemek.white,
						fontSize: small ? 14 : 20,
						fontWeight: 600,
						borderRadius: '4px',
					}}
				>
					+{overlayCount}
				</Box>
			) : null}
		</Box>
	);
};

// Reusable gallery block: title + thumbnail grid + fullscreen modal viewer.
const PhotoGallery = ({
	title,
	loading,
	photos,
}: {
	title: string;
	loading?: boolean | null;
	photos: GalleryPhoto[];
}) => {
	const { t } = useTranslation();
	const [modalOpen, setModalOpen] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);

	const handleOpenModal = (index: number) => {
		setCurrentIndex(index);
		setModalOpen(true);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
	};

	const handlePrevious = () => {
		if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
		}
	};

	const handleNext = () => {
		if (currentIndex < photos.length - 1) {
			setCurrentIndex(currentIndex + 1);
		}
	};

	return (
		<Box sx={{ mb: '20px' }}>
			<Typography sx={{ ...titleStyles }}>{title}</Typography>
			{loading ? (
				<CircularProgress />
			) : photos?.length ? (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						gap: 1,
					}}
				>
					<Box sx={{ width: '64%' }}>
						<PhotoBox
							key={photos[0]?.Id}
							photo={photos[0]}
							onClick={() => handleOpenModal(0)}
						/>
					</Box>
					<Box
						sx={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							gap: 1,
							alignItems: 'flex-start',
						}}
					>
						{photos
							?.slice(1, 4)
							.map(
								(photo: GalleryPhoto, index: number, arr: GalleryPhoto[]) => {
									const isLastVisible = index === arr.length - 1;
									const hiddenCount = photos.length - 4;
									const showOverlay = isLastVisible && hiddenCount > 0;

									return (
										<PhotoBox
											key={photo.Id}
											photo={photo}
											small
											onClick={() => handleOpenModal(index + 1)}
											overlayCount={showOverlay ? hiddenCount : undefined}
										/>
									);
								},
							)}
					</Box>
				</Box>
			) : (
				<Typography variant="inherit">{t('global.noPictures')}</Typography>
			)}
			{photos && photos.length > 0 && (
				<PhotoModal
					open={modalOpen}
					handleOpen={handleCloseModal}
					photos={photos}
					currentIndex={currentIndex}
					onPrevious={handlePrevious}
					onNext={handleNext}
				/>
			)}
		</Box>
	);
};

const InfoSpyPhotos = ({ data }: { data: any }) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	const { loading: spyLoading, photos: peakRaw } = useSelector(
		(state: RootState) => state.spy,
	);
	const { loading: resultsLoading, pictures: resultRaw } = useSelector(
		(state: RootState) => state.results,
	);

	const peakId = data?.PeakId;
	const resultId = data?.ResultId;

	useEffect(() => {
		if (peakId) {
			dispatch(getSpyPhotosStart(peakId));
		}
	}, [peakId]);

	useEffect(() => {
		if (resultId) {
			dispatch(getResultPicturesStart(resultId));
		}
	}, [resultId]);

	const peakPhotos: GalleryPhoto[] = useMemo(
		() =>
			(peakRaw || []).map((p) => ({
				Id: p.Id,
				name: p.Name,
				thumbUrl: createDataUrl(p.ThumbnailBase64, mimeFromName(p.Name)),
				fullUrl: createDataUrl(p.FileBase64, mimeFromName(p.Name)),
			})),
		[peakRaw],
	);

	const resultPictures: GalleryPhoto[] = useMemo(
		() =>
			(resultRaw || []).map((p) => ({
				Id: p.Id,
				name: `${p.Id}.${p.Type?.toLowerCase() || 'jpg'}`,
				thumbUrl: createDataUrl(p.Thumbnail, p.Type),
				fullUrl: createDataUrl(p.PicData, p.Type),
			})),
		[resultRaw],
	);

	return (
		<>
			{peakId ? (
				<PhotoGallery
					title={t('global.peakPhotos')}
					loading={spyLoading?.getSpyPhotos}
					photos={peakPhotos}
				/>
			) : null}
			{resultId ? (
				<PhotoGallery
					title={t('global.resultPictures')}
					loading={resultsLoading?.getResultPictures}
					photos={resultPictures}
				/>
			) : null}
		</>
	);
};

export default InfoSpyPhotos;
